import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './VideoGenerationDashboard.css';

const VideoGenerationDashboard = ({ userId }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [videoLogs, setVideoLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filters, setFilters] = useState({
    status: '',
    language: '',
    page: 1,
    limit: 50,
  });
  const [logs, setLogs] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const logsEndRef = useRef(null);
  const refreshInterval = useRef(null);

  // Fetch dashboard data
  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`/api/heygen/dashboard/${userId}`);
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      addLog('error', 'Failed to fetch dashboard data');
    }
  };

  // Fetch video logs
  const fetchVideoLogs = async () => {
    try {
      const params = new URLSearchParams({
        page: filters.page,
        limit: filters.limit,
        ...(filters.status && { status: filters.status }),
        ...(filters.language && { language: filters.language }),
      });

      const response = await axios.get(`/api/heygen/logs/${userId}?${params}`);
      if (response.data.success) {
        setVideoLogs(response.data.data.logs);
      }
    } catch (error) {
      console.error('Error fetching video logs:', error);
      addLog('error', 'Failed to fetch video logs');
    }
  };

  // Add log entry
  const addLog = (type, message, data = null) => {
    const logEntry = {
      id: Date.now(),
      timestamp: new Date().toLocaleTimeString(),
      type,
      message,
      data,
    };
    setLogs(prev => [...prev, logEntry].slice(-100)); // Keep last 100 logs
  };

  // Auto-scroll logs to bottom
  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Retry video generation
  const retryVideo = async (videoStatusId) => {
    try {
      addLog('info', `Retrying video generation for ${videoStatusId}`);
      const response = await axios.post(`/api/heygen/retry/${videoStatusId}`);
      
      if (response.data.success) {
        addLog('success', 'Video generation retry initiated');
        fetchDashboard();
        fetchVideoLogs();
      } else {
        addLog('error', 'Failed to retry video generation');
      }
    } catch (error) {
      console.error('Error retrying video:', error);
      addLog('error', `Error retrying video: ${error.message}`);
    }
  };

  // Check pending videos
  const checkPendingVideos = async () => {
    try {
      addLog('info', 'Checking pending videos status...');
      const response = await axios.get(`/api/heygen/check-pending-videos/${userId}`);
      
      if (response.data.success) {
        addLog('success', `Checked ${response.data.checked} pending videos, updated ${response.data.updated}`);
        fetchDashboard();
        fetchVideoLogs();
      }
    } catch (error) {
      console.error('Error checking pending videos:', error);
      addLog('error', `Error checking pending videos: ${error.message}`);
    }
  };

  // Generate videos for all locations in a language
  const generateAllVideos = async (language) => {
    try {
      addLog('info', `Starting video generation for all locations in ${language}`);
      const response = await axios.post('/api/heygen/generate-all-location-videos', {
        userId,
        language,
      });

      if (response.data.success) {
        addLog('success', `Video generation initiated for ${response.data.processed} locations`);
        fetchDashboard();
      } else {
        addLog('error', 'Failed to generate videos');
      }
    } catch (error) {
      console.error('Error generating videos:', error);
      addLog('error', `Error generating videos: ${error.message}`);
    }
  };

  // Download video to AWS
  const downloadVideoToAWS = async (videoStatusId) => {
    try {
      addLog('info', `Starting AWS download for video ${videoStatusId}`);
      const response = await axios.post(`/api/heygen/download-video/${videoStatusId}`);

      if (response.data.success) {
        addLog('success', `Video successfully downloaded to AWS`);
        fetchDashboard();
        fetchVideoLogs();
      } else {
        addLog('error', 'Failed to download video to AWS');
      }
    } catch (error) {
      console.error('Error downloading video to AWS:', error);
      addLog('error', `Error downloading video to AWS: ${error.message}`);
    }
  };

  // Download all pending videos to AWS
  const downloadAllPendingToAWS = async () => {
    try {
      addLog('info', 'Starting bulk AWS download for all pending videos');
      const response = await axios.post('/api/heygen/download-all-pending');

      if (response.data.success) {
        addLog('success', `Bulk download completed: ${response.data.successful} successful, ${response.data.errors} errors`);
        fetchDashboard();
        fetchVideoLogs();
      } else {
        addLog('error', 'Failed to start bulk download');
      }
    } catch (error) {
      console.error('Error in bulk download:', error);
      addLog('error', `Error in bulk download: ${error.message}`);
    }
  };

  // Initialize and setup auto-refresh
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchVideoLogs()]);
      setLoading(false);
      addLog('info', 'Dashboard loaded');
    };

    loadData();

    // Setup auto-refresh
    if (autoRefresh) {
      refreshInterval.current = setInterval(() => {
        fetchDashboard();
        if (activeTab === 'logs') {
          fetchVideoLogs();
        }
      }, 10000); // Refresh every 10 seconds
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [userId, autoRefresh, activeTab]);

  // Scroll logs to bottom when new logs are added
  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  // Update logs when filters change
  useEffect(() => {
    if (activeTab === 'logs') {
      fetchVideoLogs();
    }
  }, [filters]);

  if (loading) {
    return (
      <div className="video-dashboard-loading">
        <div className="spinner"></div>
        <p>Loading video generation dashboard...</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'processing': return '#ffc107';
      case 'pending': return '#17a2b8';
      case 'failed': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✅';
      case 'processing': return '⏳';
      case 'pending': return '⏸️';
      case 'failed': return '❌';
      default: return '❓';
    }
  };

  const getDownloadStatusIcon = (downloadStatus) => {
    switch (downloadStatus) {
      case 'completed': return '☁️';
      case 'downloading': return '📥';
      case 'pending': return '⏳';
      case 'failed': return '❌';
      default: return '❓';
    }
  };

  const getDownloadStatusColor = (downloadStatus) => {
    switch (downloadStatus) {
      case 'completed': return '#28a745';
      case 'downloading': return '#17a2b8';
      case 'pending': return '#ffc107';
      case 'failed': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div className="video-generation-dashboard">
      <div className="dashboard-header">
        <h2>🎬 Video Generation Dashboard</h2>
        <div className="dashboard-controls">
          <button 
            className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            📊 Dashboard
          </button>
          <button 
            className={`tab-btn ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📋 Video Logs
          </button>
          <button 
            className={`tab-btn ${activeTab === 'console' ? 'active' : ''}`}
            onClick={() => setActiveTab('console')}
          >
            🖥️ Console
          </button>
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto Refresh
          </label>
        </div>
      </div>

      {activeTab === 'dashboard' && dashboardData && (
        <div className="dashboard-content">
          {/* Summary Stats */}
          <div className="stats-grid">
            <div className="stat-card total">
              <h3>Total Videos</h3>
              <div className="stat-number">{dashboardData.stats.total}</div>
            </div>
            <div className="stat-card completed">
              <h3>Completed</h3>
              <div className="stat-number">{dashboardData.stats.completed}</div>
            </div>
            <div className="stat-card processing">
              <h3>Processing</h3>
              <div className="stat-number">{dashboardData.stats.processing}</div>
            </div>
            <div className="stat-card pending">
              <h3>Pending</h3>
              <div className="stat-number">{dashboardData.stats.pending}</div>
            </div>
            <div className="stat-card failed">
              <h3>Failed</h3>
              <div className="stat-number">{dashboardData.stats.failed}</div>
            </div>
            <div className="stat-card locations">
              <h3>Total Locations</h3>
              <div className="stat-number">{dashboardData.totalLocations}</div>
            </div>
          </div>

          {/* Language Breakdown */}
          <div className="language-breakdown">
            <h3>Videos by Language</h3>
            <div className="language-grid">
              {Object.entries(dashboardData.byLanguage).map(([lang, stats]) => (
                <div key={lang} className="language-card">
                  <div className="language-header">
                    <h4>{lang.toUpperCase()}</h4>
                    <button 
                      className="generate-btn"
                      onClick={() => generateAllVideos(lang)}
                      disabled={lang === 'en'}
                    >
                      🎬 Generate All
                    </button>
                  </div>
                  <div className="language-stats">
                    <div className="stat-item">
                      <span className="stat-label">Total:</span>
                      <span className="stat-value">{stats.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Completed:</span>
                      <span className="stat-value completed">{stats.completed}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Processing:</span>
                      <span className="stat-value processing">{stats.processing}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Failed:</span>
                      <span className="stat-value failed">{stats.failed}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <h3>Quick Actions</h3>
            <div className="action-buttons">
              <button className="action-btn primary" onClick={checkPendingVideos}>
                🔄 Check Pending Videos
              </button>
              <button className="action-btn secondary" onClick={fetchDashboard}>
                📊 Refresh Dashboard
              </button>
              <button className="action-btn aws" onClick={downloadAllPendingToAWS}>
                ☁️ Download All to AWS
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="recent-activity">
            <h3>Recent Activity</h3>
            <div className="activity-list">
              {dashboardData.recentActivity.slice(0, 10).map((activity) => (
                <div key={activity.id} className="activity-item">
                  <div className="activity-icon">
                    {getStatusIcon(activity.status)}
                  </div>
                  <div className="activity-details">
                    <div className="activity-title">
                      {activity.locationName} ({activity.language})
                    </div>
                    <div className="activity-meta">
                      <span className={`status ${activity.status}`}>
                        {activity.status}
                      </span>
                      <span className="timestamp">
                        {new Date(activity.updatedAt).toLocaleString()}
                      </span>
                    </div>
                    {activity.error && (
                      <div className="activity-error">{activity.error}</div>
                    )}
                  </div>
                  {activity.status === 'failed' && (
                    <button 
                      className="retry-btn"
                      onClick={() => retryVideo(activity.id)}
                    >
                      🔄 Retry
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="logs-content">
          {/* Filters */}
          <div className="logs-filters">
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={filters.language}
              onChange={(e) => setFilters(prev => ({ ...prev, language: e.target.value, page: 1 }))}
            >
              <option value="">All Languages</option>
              {dashboardData && Object.keys(dashboardData.byLanguage).map(lang => (
                <option key={lang} value={lang}>{lang.toUpperCase()}</option>
              ))}
            </select>
            <button onClick={fetchVideoLogs} className="refresh-logs-btn">
              🔄 Refresh Logs
            </button>
          </div>

          {/* Video Logs Table */}
          <div className="logs-table-container">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>AWS Status</th>
                  <th>Location</th>
                  <th>Language</th>
                  <th>Video ID</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Duration</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {videoLogs.map((log) => (
                  <tr key={log._id} className={`log-row ${log.status}`}>
                    <td>
                      <span 
                        className={`status-badge ${log.status}`}
                        style={{ backgroundColor: getStatusColor(log.status) }}
                      >
                        {getStatusIcon(log.status)} {log.status}
                      </span>
                    </td>
                    <td>
                      <span 
                        className={`status-badge ${log.downloadStatus || 'pending'}`}
                        style={{ backgroundColor: getDownloadStatusColor(log.downloadStatus || 'pending') }}
                        title={log.downloadError || 'AWS download status'}
                      >
                        {getDownloadStatusIcon(log.downloadStatus || 'pending')} {log.downloadStatus || 'pending'}
                      </span>
                    </td>
                    <td className="location-name">
                      {log.locationId?.name || 'Unknown'}
                    </td>
                    <td className="language">{log.language.toUpperCase()}</td>
                    <td className="video-id">
                      <code>{log.videoId}</code>
                    </td>
                    <td className="timestamp">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="timestamp">
                      {new Date(log.updatedAt).toLocaleString()}
                    </td>
                    <td className="duration">
                      {log.duration ? `${log.duration}s` : '-'}
                    </td>
                    <td className="actions">
                      {log.status === 'failed' && (
                        <button 
                          className="retry-btn small"
                          onClick={() => retryVideo(log._id)}
                          title="Retry video generation"
                        >
                          🔄
                        </button>
                      )}
                      {log.status === 'completed' && log.downloadStatus !== 'completed' && (
                        <button 
                          className="download-btn small"
                          onClick={() => downloadVideoToAWS(log._id)}
                          title="Download to AWS"
                        >
                          ☁️
                        </button>
                      )}
                      {log.awsVideoUrl && (
                        <a 
                          href={log.awsVideoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="view-btn small aws"
                          title="View AWS video"
                        >
                          ☁️👁️
                        </a>
                      )}
                      {log.videoUrl && (
                        <a 
                          href={log.videoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="view-btn small heygen"
                          title="View HeyGen video (expires in 7 days)"
                        >
                          🎬👁️
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'console' && (
        <div className="console-content">
          <div className="console-header">
            <h3>🖥️ Live Console</h3>
            <div className="console-controls">
              <button onClick={() => setLogs([])} className="clear-logs-btn">
                🗑️ Clear Logs
              </button>
              <span className="log-count">{logs.length} entries</span>
            </div>
          </div>
          <div className="console-logs">
            {logs.map((log) => (
              <div key={log.id} className={`console-log ${log.type}`}>
                <span className="log-timestamp">[{log.timestamp}]</span>
                <span className={`log-type ${log.type}`}>
                  {log.type.toUpperCase()}
                </span>
                <span className="log-message">{log.message}</span>
                {log.data && (
                  <pre className="log-data">{JSON.stringify(log.data, null, 2)}</pre>
                )}
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoGenerationDashboard;